import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { NAV_LINKS } from "../data/content";
import { InquiryCTA } from "./PlanEventModal";

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header data-testid="site-nav" className={`sticky top-0 z-50 border-b transition-[background-color,border-color] duration-300 ${scrolled || open ? "border-black/10 bg-white/90 shadow-[0_6px_24px_-16px_rgba(0,0,0,0.3)] backdrop-blur-xl" : "border-transparent bg-white/80 backdrop-blur-md"}`}>
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center" aria-label="Entertainment Nation home">
          <img src="/images/logo.png" alt="Entertainment Nation" width="708" height="113" fetchPriority="high" className="h-7 w-auto sm:h-8" />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} end={l.to === "/"} data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} data-track={`nav_${l.label.toLowerCase().replace(/[^a-z]+/g, "_")}`}
              className={({ isActive }) => `relative whitespace-nowrap rounded-full px-3 py-2 font-gothic uppercase tracking-wider text-[12px] xl:px-4 xl:text-[13px] transition-colors duration-200 ${isActive ? "text-en-teal" : "text-black/70 hover:text-black"}`}>
              {l.label}
            </NavLink>
          ))}
          <InquiryCTA label="Get Started" data-testid="nav-plan-event-btn" className="btn-primary ml-2 whitespace-nowrap !py-2.5 !px-5" />
        </nav>

        <button data-testid="nav-mobile-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-label="Toggle menu"
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-black/15 text-black lg:hidden">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <div aria-hidden data-testid="nav-accent-bar" className={`pointer-events-none absolute inset-x-0 bottom-0 h-0.5 origin-center bg-en-yellow transition-[opacity,transform] duration-200 ${scrolled ? "scale-x-0 opacity-0" : "scale-x-100 opacity-100"}`} />

      <AnimatePresence>
        {open && (
          <motion.nav data-testid="nav-mobile-menu" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.25 }}
            className="absolute inset-x-0 top-full overflow-hidden border-t border-black/10 bg-white/95 shadow-xl backdrop-blur-xl lg:hidden" aria-label="Mobile">
            <div className="flex flex-col gap-1 px-5 py-5">
              {NAV_LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <NavLink to={l.to} end={l.to === "/"} data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={({ isActive }) => `block rounded-lg px-3 py-3 font-display text-2xl ${isActive ? "text-en-teal" : "text-black"}`}>
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <InquiryCTA label="Get Started" onClick={() => setOpen(false)} data-testid="mobile-nav-plan-event-btn" className="btn-primary mt-4 w-full" />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
